import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';

interface DialogOriginSummaryEntry {
  label: string;
  value: string;
}

export interface DialogOrigin {
  kiDiaId?: string;
  formType?: string;
  dialogTitle?: string;
  title?: string;
  transactionTypeHint?: string;
  submittedAt?: string;
  summary?: DialogOriginSummaryEntry[];
  context?: Record<string, unknown>;
  rawData?: Record<string, unknown>;
}

interface Props {
  dialogOrigin: DialogOrigin;
}

const formatDate = (iso?: string) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
};

const looksLikeJson = (val: string): boolean => {
  const v = val.trim();
  return v.startsWith('[{') || v.startsWith('{') || v.startsWith('[');
};

const formatRawValue = (val: unknown): string => {
  if (val === null || val === undefined || val === '') return '–';
  if (typeof val === 'boolean') return val ? 'Ja' : 'Nein';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

export const DialogOriginPanel: React.FC<Props> = ({ dialogOrigin }) => {
  // Defensiv: Einträge mit JSON-Array/-Objekt als Wert ausblenden,
  // diese stammen aus älteren ki-dia-Versionen ohne Filter.
  const summary = (dialogOrigin.summary ?? []).filter((e) => {
    const v = (e.value ?? '').trim();
    if (!v) return false;
    return !looksLikeJson(v);
  });
  const rawData = dialogOrigin.rawData ?? {};
  const rawEntries = Object.entries(rawData).filter(([, v]) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object') return false;
    if (typeof v === 'string' && looksLikeJson(v)) return false;
    return true;
  });

  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          Dialog-Eingang
        </Typography>
        {dialogOrigin.formType && (
          <Chip size='small' label={dialogOrigin.formType} />
        )}
        {dialogOrigin.dialogTitle && (
          <Chip
            size='small'
            label={dialogOrigin.dialogTitle}
            variant='outlined'
          />
        )}
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        {dialogOrigin.title}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        Eingegangen: {formatDate(dialogOrigin.submittedAt)}
        {dialogOrigin.transactionTypeHint
          ? ` · Vorschlag: ${dialogOrigin.transactionTypeHint}`
          : ''}
        {dialogOrigin.kiDiaId ? ` · ID: ${dialogOrigin.kiDiaId}` : ''}
      </Typography>

      {summary.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant='subtitle2' sx={{ mb: 1 }}>
            Dialog-Informationen
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr',
              rowGap: 0.5,
              columnGap: 2,
            }}
          >
            {summary.map((entry, idx) => (
              <React.Fragment key={`${entry.label}-${idx}`}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ fontWeight: 500 }}
                >
                  {entry.label}
                </Typography>
                <Typography variant='body2'>{entry.value}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </>
      )}

      {rawEntries.length > 0 && (
        <Accordion sx={{ mt: 2 }} disableGutters elevation={0} square>
          <AccordionSummary expandIcon={<GridExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant='subtitle2'>Rohdaten anzeigen</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '180px 1fr',
                rowGap: 0.5,
                columnGap: 2,
              }}
            >
              {rawEntries.map(([key, value]) => (
                <React.Fragment key={key}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {key}
                  </Typography>
                  <Typography variant='body2'>
                    {formatRawValue(value)}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};
